<template>
    <div>
        <!---
        You can use the `control` object - basically, it's main data of your custom control field
        --->

        <div :class="styles.FORM.FORM_GROUP">
            <div class="wiki-config-header">
                <i class="fa fa-file-text-o"></i>
                <span>Wiki Content Editor (English)</span>
            </div>
            <p class="wiki-config-hint">
                Use the editor below to add formatted content. Changes are auto-saved.
            </p>

            <div class="wiki-editor-container">
                <CustomEditor
                      ref="wikiEditor"
                      :editorData="this.editorData"
                      :editorTools="editorTools"
                      :hideSaveButton="true"
                      @save="save"
                      @onReady="onReady"
                      >
                </CustomEditor>
            </div>
        </div>

        <!-- Translation Wiki Content Editors (one per enabled language) -->
        <div :class="styles.FORM.FORM_GROUP" v-for="lang in enabledTranslationLanguages" :key="'wiki-' + lang.code">
            <div class="wiki-config-header wiki-config-header-translation">
                <i class="fa fa-file-text-o"></i>
                <span>Wiki Content Editor ({{ lang.name }})</span>
                <button
                    type="button"
                    class="btn-translate-wiki"
                    @click="translateWikiContent(lang.code, lang.suffix)"
                    :disabled="translatingLang === lang.code || !hasEnglishContent"
                    :title="!hasEnglishContent ? 'Add English content first' : 'Translate English content to ' + lang.name"
                >
                    <i :class="translatingLang === lang.code ? 'fa fa-spinner fa-spin' : 'fa fa-language'"></i>
                    {{ translatingLang === lang.code ? 'Translating...' : 'Translate' }}
                </button>
            </div>
            <p class="wiki-config-hint">
                Add {{ lang.name }} translation of the wiki content, or click "Translate" to auto-translate from English. Changes are auto-saved.
            </p>

            <div class="wiki-editor-container wiki-editor-container-translation">
                <CustomEditor
                      :ref="'wikiEditor' + lang.suffix"
                      :editorData="getTranslationEditorData(lang.suffix)"
                      :editorTools="editorTools"
                      :hideSaveButton="true"
                      @save="(e) => saveTranslation(e, lang.suffix)"
                      @onReady="() => {}"
                      :key="translationEditorKeys[lang.suffix] || 0"
                      >
                </CustomEditor>
            </div>
        </div>
    </div>
</template>

<script>
    import {CONTROL_SPECIAL_CONFIG_MIXIN} from "../../mixins/control-special-config-mixin";
    import BaseControlConfigSkeleton from "../../skeletons/controls/BaseControlConfigSkeleton";
    import CustomEditor from "@/components/Editor/Editor.vue";
    import {EVENT_CONSTANTS} from "../../configs/events";
    import axios from 'axios';
    import { uploadToS3 } from "@/utils/fileUpload";

    export default {
        name: "WikiConfigView",
        extends: BaseControlConfigSkeleton,
        components: { CustomEditor },
        mixins: [CONTROL_SPECIAL_CONFIG_MIXIN],
        data:() => ({
            saveInterval: null,
            translatingLang: null,
            translationEditorKeys: {},
            initData: {
                blocks: [
                    {
                        type: "header",
                        data: { text: "Enter your html text...", level: 2 }
                    }
                ]
            },
            editorTools: {
                header: {
                    class: "Header",
                    inlineToolbar: ["marker", "italic", "bold", "link"]
                },

                // Insert-image support (file upload, drag-and-drop, paste, URL).
                // Reuses the image tool already built into the shared CustomEditor.
                // teamioType MUST be 'FORM' so the image lands in the public + durable
                // forms/{siteID}/ S3 prefix (loads for the gated/anon patient and the
                // headless PDF render). Do NOT use 'WIKI' — that routes to temp/, which
                // auto-deletes after 30 days and would silently break these images.
                image: {
                    config: {
                        uploader: {
                            async uploadByFile(file) {
                                const result = await uploadToS3({
                                    file,
                                    siteID: window.localStorage.teamioSiteID,
                                    teamioType: 'FORM'
                                });
                                return {
                                    success: 1,
                                    file: { url: result.url, name: result.name, size: result.size }
                                };
                            },
                            async uploadByUrl(url) {
                                // Reject non-HTTP URLs
                                if (!url || !url.match(/^https?:\/\/.+/i)) {
                                    return { success: 0, message: 'Invalid image URL' };
                                }
                                try {
                                    // Re-host the external image on our S3 so it can't expire
                                    // out from under the form (e.g. signed/temporary URLs).
                                    const response = await fetch(url);
                                    if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);

                                    const blob = await response.blob();

                                    let fileName;
                                    try {
                                        fileName = decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
                                    } catch (e) {
                                        fileName = '';
                                    }
                                    if (!fileName || fileName === '/' || !fileName.includes('.')) {
                                        fileName = `pasted-image-${Date.now()}.png`;
                                    }

                                    const file = new File([blob], fileName, { type: blob.type || 'image/png' });

                                    const result = await uploadToS3({
                                        file,
                                        siteID: window.localStorage.teamioSiteID,
                                        teamioType: 'FORM'
                                    });

                                    return {
                                        success: 1,
                                        file: { url: result.url, name: result.name, size: result.size }
                                    };
                                } catch (error) {
                                    console.warn('uploadByUrl: Could not re-upload to S3, falling back to external URL.', error);
                                    // Preserve the external URL rather than losing the image entirely
                                    return { success: 1, file: { url: url } };
                                }
                            }
                        }
                    }
                },

                embed: {
                    class: "Embed",
                    inlineToolbar: true
                },
                list: {
                    class: "List",
                    inlineToolbar: true
                },
                delimiter: { class: "Delimiter" },
                table: {
                    class: "Table",
                    inlineToolbar: true
                },
                marker: {
                    class: "Marker",
                    shortcut: "CMD+SHIFT+M"
                },
                inlineCode: {
                    class: "InlineCode",
                    shortcut: "CMD+SHIFT+C"
                },
                paragraph: {
                    class: "Paragraph",
                    inlineToolbar: true
                },
                checklist: {
                    class: "Checklist",
                    inlineToolbar: true
                },
            },
        }),

        created() {},

        mounted() {
            // Start auto-save timer - saves every 2 seconds
            this.saveInterval = setInterval(() => {
                this.manualSave();
                // Auto-save all translation editors
                for (const lang of this.enabledTranslationLanguages) {
                    this.manualSaveTranslation(lang.suffix);
                }
            }, 2000);
        },

        beforeDestroy() {
            // Clear the auto-save interval
            if (this.saveInterval) {
                clearInterval(this.saveInterval);
            }

            // Do one final save
            this.manualSave();
            for (const lang of this.enabledTranslationLanguages) {
                this.manualSaveTranslation(lang.suffix);
            }
        },

        watch: {
            control: {
                deep: true,
                handler(controlObj) {},
            },
            value(val) {
                this.setValue(val);
            }
        },

        methods: {
            // Manual save trigger - called when user wants to save
            async manualSave() {
                if (this.$refs.wikiEditor && this.$refs.wikiEditor.editor) {
                    try {
                        const outputData = await this.$refs.wikiEditor.editor.save();
                        this.save(outputData);
                    } catch (error) {
                        console.error('WikiConfigView manualSave - error:', error);
                    }
                }
            },

            // Manual save trigger for a translation editor
            async manualSaveTranslation(suffix) {
                const refName = 'wikiEditor' + suffix;
                const editorRef = this.$refs[refName];
                // Dynamic refs inside v-for may be arrays in Vue 2
                const editor = Array.isArray(editorRef) ? editorRef[0] : editorRef;
                if (editor && editor.editor) {
                    try {
                        const outputData = await editor.editor.save();
                        this.saveTranslation(outputData, suffix);
                    } catch (error) {
                        console.error(`WikiConfigView manualSave${suffix} - error:`, error);
                    }
                }
            },

            onReady() {},

            onChange: () => {},

            escapeRegExp(string) {
                return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
            },

            replaceAll(str, find, replace) {
                return str.replace(new RegExp(this.escapeRegExp(find), 'g'), replace);
            },

            save(e) {
                let allpara = e.blocks.filter(b => b.type === 'paragraph');
                let _this = this;
                allpara.forEach(function(b) {
                    b.data.text = _this.replaceAll(b.data.text, '&nbsp;', '');
                });

                const jsonString = JSON.stringify(e);

                // Use Vue.set to ensure reactivity
                this.$set(this.control, 'wikiContent', jsonString);
            },

            // Save translation wiki content for a given language suffix
            saveTranslation(e, suffix) {
                let allpara = e.blocks.filter(b => b.type === 'paragraph');
                let _this = this;
                allpara.forEach(function(b) {
                    b.data.text = _this.replaceAll(b.data.text, '&nbsp;', '');
                });

                const jsonString = JSON.stringify(e);
                const contentKey = 'wikiContent' + suffix;
                const manualKey = 'wikiContent' + suffix + 'Manual';

                // Use Vue.set to ensure reactivity
                this.$set(this.control, contentKey, jsonString);

                // Mark as manually edited so auto-translation doesn't overwrite on save
                if (!this.translatingLang) {
                    this.$set(this.control, manualKey, true);
                }
            },

            /**
             * Get editor data for a translation language
             */
            getTranslationEditorData(suffix) {
                const contentKey = 'wikiContent' + suffix;
                if (this.control[contentKey] && this.control[contentKey] !== '') {
                    try {
                        return JSON.parse(this.control[contentKey]);
                    } catch (e) {
                        return this.initData;
                    }
                }
                return this.initData;
            },

            /**
             * Translate wiki content from English to target language using AWS Translate
             */
            async translateWikiContent(langCode, suffix) {
                if (!this.hasEnglishContent) {
                    console.warn('No English content to translate');
                    return;
                }

                this.translatingLang = langCode;

                try {
                    // Parse the English wiki content
                    const englishData = JSON.parse(this.control.wikiContent);

                    // Extract all text blocks and translate them
                    const translatedBlocks = await this.translateEditorBlocks(englishData.blocks, langCode);

                    // Create the translated content structure
                    const translatedData = {
                        ...englishData,
                        blocks: translatedBlocks
                    };

                    // Save the translated content
                    const jsonString = JSON.stringify(translatedData);
                    const contentKey = 'wikiContent' + suffix;
                    const manualKey = 'wikiContent' + suffix + 'Manual';

                    this.$set(this.control, contentKey, jsonString);

                    // Mark as NOT manually edited since this is auto-translation
                    this.$set(this.control, manualKey, false);

                    // Force the editor to re-render with new content
                    this.$set(this.translationEditorKeys, suffix, Date.now());

                    console.log(`Wiki content translated to ${langCode} successfully`);
                } catch (err) {
                    console.error('Wiki translation error:', err);
                    alert('Translation failed: ' + (err.message || 'Unknown error'));
                } finally {
                    this.translatingLang = null;
                }
            },

            /**
             * Translate Editor.js blocks using backend API
             */
            async translateEditorBlocks(blocks, langCode) {
                const translatedBlocks = [];

                for (const block of blocks) {
                    const translatedBlock = { ...block, data: { ...block.data } };

                    // Translate text content based on block type
                    if (block.type === 'paragraph' && block.data.text) {
                        translatedBlock.data.text = await this.translateText(block.data.text, langCode);
                    } else if (block.type === 'header' && block.data.text) {
                        translatedBlock.data.text = await this.translateText(block.data.text, langCode);
                    } else if (block.type === 'list' && block.data.items) {
                        translatedBlock.data.items = await Promise.all(
                            block.data.items.map(item => this.translateText(item, langCode))
                        );
                    } else if (block.type === 'checklist' && block.data.items) {
                        translatedBlock.data.items = await Promise.all(
                            block.data.items.map(async (item) => ({
                                ...item,
                                text: await this.translateText(item.text, langCode)
                            }))
                        );
                    } else if (block.type === 'table' && block.data.content) {
                        translatedBlock.data.content = await Promise.all(
                            block.data.content.map(async (row) =>
                                Promise.all(row.map(cell => this.translateText(cell, langCode)))
                            )
                        );
                    }

                    translatedBlocks.push(translatedBlock);
                }

                return translatedBlocks;
            },

            /**
             * Translate a single text string using the backend API
             */
            async translateText(text, langCode) {
                if (!text || text.trim() === '') return text;

                try {
                    const response = await axios.post(
                        `${process.env.VUE_APP_TEAMIO_API_URL}/translate-text`,
                        {
                            text: text,
                            targetLanguage: langCode
                        },
                        { crossdomain: true }
                    );

                    if (response.data && response.data.translatedText) {
                        return response.data.translatedText;
                    }
                    return text;
                } catch (err) {
                    console.error('Text translation error:', err);
                    return text; // Return original on error
                }
            }

        },

        computed: {
            editorData() {
                if(this.control.wikiContent && this.control.wikiContent !== '')
                    return JSON.parse(this.control.wikiContent);
                else
                    return this.initData;
            },

            // Check if there's English content to translate (not just the default placeholder)
            hasEnglishContent() {
                if (!this.control.wikiContent || this.control.wikiContent === '') {
                    return false;
                }
                try {
                    const content = JSON.parse(this.control.wikiContent);
                    // Check if it has blocks and at least one block has non-default text
                    if (content.blocks && content.blocks.length > 0) {
                        return content.blocks.some(block => {
                            if (block.data && block.data.text) {
                                // Check it's not the default placeholder
                                return block.data.text !== 'Enter your html text...';
                            }
                            return false;
                        });
                    }
                    return false;
                } catch (e) {
                    return false;
                }
            }
        }
    }
</script>

<style scoped>
.wiki-config-header {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 2px solid #3498db;
}

.wiki-config-header i:first-child {
    color: #3498db;
    font-size: 18px;
}

/* Translate button */
.btn-translate-wiki {
    margin-left: auto;
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 500;
    color: #fff;
    background-color: #22c55e;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: background-color 0.2s, opacity 0.2s;
}

.btn-translate-wiki:hover:not(:disabled) {
    background-color: #16a34a;
}

.btn-translate-wiki:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-translate-wiki i {
    font-size: 14px;
    color: #fff !important;
}

.wiki-config-hint {
    font-size: 13px;
    color: #6c757d;
    margin-bottom: 12px;
    font-style: italic;
}

.wiki-editor-container {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 15px;
    background-color: #fff;
    min-height: 300px;
    max-height: 500px;
    overflow-y: auto;
}

.wiki-editor-container:focus-within {
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}

/* Remove gray background from Editor.js elements */
.wiki-editor-container >>> .abc {
    background-color: #fff;
}

.wiki-editor-container >>> .codex-editor {
    background-color: transparent;
}

.wiki-editor-container >>> .codex-editor__redactor {
    background-color: transparent;
    padding-bottom: 100px !important;
}

.wiki-editor-container >>> .ce-block {
    background-color: transparent;
}

.wiki-editor-container >>> .ce-block__content {
    background-color: transparent;
    max-width: 100%;
}

/* Bullet and numbered lists styling */
.wiki-editor-container >>> ul,
.wiki-editor-container >>> ol {
    margin: 10px 0;
    padding-left: 25px;
}

.wiki-editor-container >>> ul {
    list-style-type: disc;
}

.wiki-editor-container >>> ol {
    list-style-type: decimal;
}

.wiki-editor-container >>> li {
    margin-bottom: 5px;
    line-height: 1.5;
}

/* Translation editor styles */
.wiki-config-header-translation {
    border-bottom-color: #22c55e;
    margin-top: 20px;
}

.wiki-config-header-translation i {
    color: #22c55e;
}

.wiki-editor-container-translation {
    border-color: #86efac;
}

.wiki-editor-container-translation:focus-within {
    border-color: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
}
</style>
